import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useTenant } from '@/contexts/TenantContext';
import { appConfig } from '@/config/environment';

/**
 * Permission and Role Types
 */
type EnterpriseRole =
  | 'super_admin'
  | 'system_admin'
  | 'entity_admin'
  | 'compliance_officer'
  | 'legal_counsel'
  | 'board_member'
  | 'executive'
  | 'manager'
  | 'employee'
  | 'external_auditor'
  | 'guest';

type PermissionType =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'approve'
  | 'publish'
  | 'manage_users'
  | 'manage_roles'
  | 'view_audit'
  | 'export_data'
  | 'manage_settings'
  | 'manage_integrations';

interface UserRole {
  role: EnterpriseRole;
  entity_id: string | null;
}

interface UsePermissionsReturn {
  roles: UserRole[];
  loading: boolean;
  error: string | null;
  hasPermission: (permission: PermissionType, resourceType: string, entityId?: string) => Promise<boolean>;
  hasRole: (role: EnterpriseRole, entityId?: string) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  refetch: () => Promise<void>;
}

/** Sentinel UUID used when no real tenant is known — never send to backend */
const SENTINEL_TENANT_ID = '00000000-0000-0000-0000-000000000000';

/**
 * API client helper
 */
const apiRequest = async <T>(endpoint: string, options: RequestInit = {}, tenantId?: string): Promise<T> => {
  const apiUrl = `${appConfig.API_URL || ''}/api${endpoint}`;
  const tenantHeader: Record<string, string> =
    tenantId && tenantId !== SENTINEL_TENANT_ID ? { 'X-Tenant-ID': tenantId } : {};
  const response = await fetch(apiUrl, {
    credentials: 'include', // Required for session cookie authentication
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...tenantHeader, // Tenant header last — cannot be overridden by callers
    },
    ...options,
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`);
  }

  const json = await response.json();
  // Guard against error-shaped responses that passed with 200 status
  if (json && typeof json === 'object' && 'error' in json && !('data' in json)) {
    throw new Error(`API error: ${json.error}`);
  }
  return json as T;
};

/**
 * usePermissions Hook - Migrated to use API endpoints
 * Uses Cloudflare Workers API
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!user) {
      setRoles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API endpoint
      const response = await apiRequest<{ data: UserRole[] }>(`/services/user-roles?user_id=${user.id}`, {}, tenant?.id);
      setRoles(response.data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch user roles';
      setError(message);
      console.error('Error fetching user roles:', err);
      // Set default empty roles on error
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [user, tenant?.id]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const hasPermission = useCallback(async (
    permission: PermissionType,
    resourceType: string,
    entityId?: string
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      // Call API endpoint
      const response = await apiRequest<{ data: boolean }>('/services/check-permission', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user.id,
          permission,
          resource_type: resourceType,
          entity_id: entityId || null
        }),
      }, tenant?.id);

      return response.data === true;
    } catch (err) {
      console.error('Error checking permission:', err);
      return false;
    }
  }, [user, tenant?.id]);

  const hasRole = useCallback((role: EnterpriseRole, entityId?: string): boolean => {
    if (!roles.length) return false;

    return roles.some(r => {
      if (r.role !== role) return false;
      if (entityId && r.entity_id !== entityId) return false;
      return true;
    });
  }, [roles]);

  const isAdmin = roles.some(r =>
    r.role === 'super_admin' || r.role === 'system_admin'
  );

  const isSuperAdmin = roles.some(r => r.role === 'super_admin');

  return {
    roles,
    loading,
    error,
    hasPermission,
    hasRole,
    isAdmin,
    isSuperAdmin,
    refetch: fetchRoles
  };
};

export default usePermissions;
